import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Zap, Loader2, HardDrive } from 'lucide-react';
import PanelShell from './PanelShell';
import EnergyTank from '../energy/EnergyTank';
import EnergyRangePicker, { CYCLE_RANGE } from '../energy/EnergyRangePicker';
import PizzaChart from '../energy/PizzaChart';
import PillarBreakdown from '../energy/PillarBreakdown';
import AtomicList from '../energy/AtomicList';
import EnergyBreadcrumb from '../energy/EnergyBreadcrumb';
import EnergyLockdownBanner from '../energy/EnergyLockdownBanner';
import DailyEnergyChart from '../energy/DailyEnergyChart';
import DailyEnergyDayView from '../energy/DailyEnergyDayView';
import GrayStackModal from '../energy/GrayStackModal';
import ContextThermostat from '../energy/ContextThermostat';
import { categoryLabel, pillarLabel } from '../energy/energyLabels';
import useEnergyTank from '../../hooks/useEnergyTank';
import { energyService } from '../../services/energyService';
import { useLanguage } from '../../i18n';
import { formatBytes } from '../../utils/formatBytes';
import { logger } from '../../utils/logger';

// ─────────────────────────────────────────────────────────────────────────
// EnergyPanel — the Energy Hub (Energy HUB part 1, Slice D).
//
// One tank, one range, three drill levels:
//   L1  the Pizza Chart (all five pillars)
//   L2  one pillar's categories
//   L3  the atomic ledger for one category
//
// Between the range picker and the drill card sits the DAILY chart (#385): one
// bar per day of the same range. Clicking a bar opens that day inside the drill
// card (the "day view"), and from there L2/L3 are read through a one-day range —
// so the drill is overview → day → pillar → category, all over the same rows.
// The chart and the pizza load and fail INDEPENDENTLY: one broken request must
// never blank the other.
//
// The tank and the range picker stay pinned at every level — the range is what
// L2 and L3 are read through, so hiding it while drilled in would make the
// numbers unexplainable.
//
// Responsiveness is CONTAINER-driven (a ResizeObserver on the body, never a
// viewport breakpoint): this panel is routinely a narrow docked column on a
// wide screen, where `md:` is simply the wrong signal (#142/#243).
// ─────────────────────────────────────────────────────────────────────────

const STACK_BREAKPOINT = 560;

const StorageLine = ({ tank, t }) => {
    const size = formatBytes(tank?.footprint_bytes);
    if (size == null) return null;
    const days = Number(tank?.storage_days_reserved);
    const parts = [`${t('energyStorage', 'Storage')}: ${size}`];
    if (Number.isFinite(days)) parts.push(t('energyStorageReserved', '{n} days reserved', { n: days }));
    if (tank?.clean_slate_at) parts.push(`${t('energyCleanSlate', 'Clean slate')}: ${String(tank.clean_slate_at).slice(0, 10)}`);
    return (
        <div
            data-testid="energy-storage"
            className="flex items-center gap-1.5 text-[12.5px] text-pai-meta min-w-0 max-w-full"
        >
            <HardDrive size={13} className="flex-shrink-0" />
            <span className="break-words min-w-0 tnum">{parts.join(' · ')}</span>
        </div>
    );
};

/**
 * The GLOBAL memory budget — what NEW conversations default to.
 *
 * Its own loader rather than a prop: the thermostat is independent of the
 * range/drill state around it, and a failure to read it must not blank the
 * statement (the card simply doesn't render).
 */
const GlobalThermostatCard = ({ t }) => {
    const [budget, setBudget] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const load = useCallback(async () => {
        try {
            setBudget(await energyService.getContextBudget());
        } catch (err) {
            logger.warn('ENERGY', `context budget load failed: ${err?.message || err}`);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (!budget) return null;

    const apply = async (tokens) => {
        setSaving(true);
        setSaveError(null);
        try {
            await energyService.setGlobalContextBudget(tokens);
            await load();   // the server normalizes what it stores — re-read it
            return true;
        } catch (err) {
            logger.warn('ENERGY', `context budget save failed: ${err?.message || err}`);
            setSaveError(t('thermostatSaveFailed', "Couldn't save that budget."));
            return false;
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="energy-thermostat" className="pai-card px-6 py-5 min-w-0 max-w-full">
            <ContextThermostat
                scope="global"
                limits={budget.limits}
                value={budget.user?.tokens ?? null}
                effectiveTokens={budget.effective?.tokens}
                busy={saving}
                error={saveError}
                onApply={apply}
            />
            <p className="mt-3 text-[12px] text-pai-meta break-words">
                {t('thermostatGlobalHint', 'Applies to new conversations. Each chat can override it from its Energy Receipt.')}
            </p>
        </div>
    );
};

const EnergyPanel = ({ mode = 'split', onClose, onToggleExpand }) => {
    const { t, language } = useLanguage();
    const { tank, loading: tankLoading, error: tankError, locked } = useEnergyTank(true);

    // The range object's identity only changes when the user picks a new one,
    // which is exactly when the drill views must refetch — so it can be a plain
    // effect dependency downstream.
    const [range, setRange] = useState(CYCLE_RANGE);

    // ONE object holds the whole drill position, so a level can never disagree
    // with the pillar/category it is meant to be showing. `day` (#385) is set
    // when the drill was entered from a bar: level 1 + day is the day view, and
    // levels 2/3 are then scoped to that single day.
    const [drill, setDrill] = useState({ level: 1, pillar: null, category: null, day: null });

    // The range L2/L3 are read through. Memoized on the DAY so its identity only
    // changes when the scope does — the drill components refetch on identity.
    const drillRange = useMemo(
        () => (drill.day ? { preset: 'custom', startDate: drill.day, endDate: drill.day } : range),
        [drill.day, range],
    );

    // A new range invalidates a selected day (it may not even be in it).
    const changeRange = useCallback((next) => {
        setRange(next);
        setDrill((d) => (d.day ? { level: 1, pillar: null, category: null, day: null } : d));
    }, []);

    // The Gray Stack's own drill-down (#302) — a modal rather than a fourth
    // level, because it answers a question about the TANK, not about the range.
    const [grayStackOpen, setGrayStackOpen] = useState(false);

    const [daily, setDaily] = useState(null);
    const [dailyLoading, setDailyLoading] = useState(true);
    const [dailyError, setDailyError] = useState(null);
    const dailyReqRef = useRef(0);

    const [pillars, setPillars] = useState(null);
    const [pillarsLoading, setPillarsLoading] = useState(true);
    const [pillarsError, setPillarsError] = useState(null);
    const reqRef = useRef(0);

    const bodyRef = useRef(null);
    const [stacked, setStacked] = useState(false);
    useEffect(() => {
        const el = bodyRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const w = entry.contentRect?.width ?? el.clientWidth;
                setStacked(w < STACK_BREAKPOINT);
            }
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const loadPillars = useCallback(() => {
        const ticket = ++reqRef.current;
        setPillarsLoading(true);
        setPillarsError(null);
        energyService
            .getPillars(range)
            .then((res) => {
                if (ticket !== reqRef.current) return;      // last-ISSUED-wins
                setPillars(res || null);
                setPillarsLoading(false);
            })
            .catch((err) => {
                if (ticket !== reqRef.current) return;
                logger.warn('ENERGY', `pillars load failed: ${err?.message || err}`);
                setPillarsError(err);
                setPillarsLoading(false);
            });
    }, [range]);

    useEffect(() => { loadPillars(); }, [loadPillars]);

    // Same params as the pizza, its own ticket and its own error: the two load
    // side by side and neither can blank the other.
    const loadDaily = useCallback(() => {
        const ticket = ++dailyReqRef.current;
        setDailyLoading(true);
        setDailyError(null);
        energyService
            .getDaily(range)
            .then((res) => {
                if (ticket !== dailyReqRef.current) return;      // last-ISSUED-wins
                setDaily(res || null);
                setDailyLoading(false);
            })
            .catch((err) => {
                if (ticket !== dailyReqRef.current) return;
                logger.warn('ENERGY', `daily load failed: ${err?.message || err}`);
                setDailyError(err);
                setDailyLoading(false);
            });
    }, [range]);

    useEffect(() => { loadDaily(); }, [loadDaily]);

    const goOverview = () => setDrill({ level: 1, pillar: null, category: null, day: null });
    const goDay = (day) => setDrill({ level: 1, pillar: null, category: null, day });
    const goPillar = (pillar) => setDrill((d) => ({ ...d, level: 2, pillar: pillar ?? d.pillar, category: null }));
    const goCategory = (category) => setDrill((d) => ({ ...d, level: 3, category }));

    // The day view reads straight from the chart's payload — no second request.
    const dayEntry = drill.day ? (daily?.days || []).find((d) => d.date === drill.day) || null : null;
    const dayCrumb = useMemo(() => {
        if (!drill.day) return null;
        try {
            return new Intl.DateTimeFormat(language === 'es' ? 'es' : 'en', {
                month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
            }).format(new Date(`${drill.day}T00:00:00Z`));
        } catch {
            return drill.day;
        }
    }, [drill.day, language]);

    return (
        <PanelShell
            title={t('energy', 'Energy Tank')}
            icon={<Zap size={18} />}
            mode={mode}
            onClose={onClose}
            onToggleExpand={onToggleExpand}
        >
            <div
                ref={bodyRef}
                data-testid="energy-panel"
                data-stacked={stacked ? 'true' : 'false'}
                className="min-h-0 min-w-0 max-w-full px-7 pb-7 space-y-4"
            >
                {locked && <EnergyLockdownBanner tank={tank} />}

                {tankError && !tank ? (
                    <div
                        data-testid="energy-tank-error"
                        className="pai-card p-5 text-[13px] text-pai-meta break-words min-w-0"
                    >
                        {t('energyError', "Couldn't load your energy data.")}
                    </div>
                ) : (
                    <EnergyTank tank={tank} onOpenGrayStack={() => setGrayStackOpen(true)} />
                )}

                {grayStackOpen && <GrayStackModal onClose={() => setGrayStackOpen(false)} />}

                {tankLoading && !tank && !tankError && (
                    <div className="flex items-center gap-2 text-[13px] text-pai-meta">
                        <Loader2 size={14} className="animate-spin" />
                        {t('energyLoading', 'Loading…')}
                    </div>
                )}

                <StorageLine tank={tank} t={t} />

                <EnergyRangePicker value={range} onChange={changeRange} />

                {/* Daily chart (#385) — between the cycle dates and the pizza. */}
                <DailyEnergyChart
                    data={daily}
                    loading={dailyLoading}
                    error={dailyError}
                    selectedDate={drill.day}
                    onSelectDay={goDay}
                />

                <EnergyBreadcrumb
                    level={drill.level}
                    // The day view has its own back control; the crumb trail only
                    // appears once the user is BELOW the day (a pillar of it).
                    dayLabel={drill.day && drill.level >= 2 ? dayCrumb : null}
                    pillarLabel={drill.pillar ? pillarLabel(t, drill.pillar) : null}
                    categoryLabel={drill.category ? categoryLabel(t, drill.category) : null}
                    onOverview={goOverview}
                    onDay={() => goDay(drill.day)}
                    onPillar={() => goPillar()}
                />

                {/* The drill surface. One card holds whichever level is open, so
                    stepping in and out never changes the page's shape. */}
                <div className="pai-card px-6 py-5 min-w-0 max-w-full">
                {drill.level === 1 && drill.day && (
                    dayEntry ? (
                        <DailyEnergyDayView day={dayEntry} onBack={goOverview} onSelectPillar={goPillar} />
                    ) : (
                        <div className="flex items-center gap-2 py-4 text-[13px] text-pai-meta">
                            <Loader2 size={14} className="animate-spin" />
                            {t('energyLoading', 'Loading…')}
                        </div>
                    )
                )}

                {drill.level === 1 && !drill.day && (
                    <>
                        {pillarsLoading && (
                                <div className="flex items-center gap-2 py-4 text-[13px] text-pai-meta">
                                <Loader2 size={14} className="animate-spin" />
                                {t('energyLoading', 'Loading…')}
                            </div>
                        )}
                        {!pillarsLoading && pillarsError && (
                                <div data-testid="energy-pillars-error" className="py-4 text-[13px] text-pai-red break-words">
                                {t('energyError', "Couldn't load your energy data.")}
                            </div>
                        )}
                        {!pillarsLoading && !pillarsError && (
                            <PizzaChart data={pillars} onSelectPillar={goPillar} stacked={stacked} />
                        )}
                    </>
                )}

                {drill.level === 2 && drill.pillar && (
                    <PillarBreakdown pillar={drill.pillar} range={drillRange} onSelectCategory={goCategory} />
                )}

                {drill.level === 3 && drill.pillar && (
                    <AtomicList pillar={drill.pillar} category={drill.category} range={drillRange} />
                )}
            </div>

                {/* The GLOBAL Context Thermostat (#349). Sits with the pillar
                    drill-down because Input Context is the Reasoning slice's
                    biggest line and this is the only control over it.
                    ADAPTED from the card's "inside the Reasoning pillar area":
                    it is rendered here ALWAYS rather than only once the user has
                    drilled into Reasoning — a setting nobody can find is a
                    setting nobody has. The per-CHAT twin lives in the receipt. */}
                <GlobalThermostatCard t={t} />
            </div>
        </PanelShell>
    );
};

export default EnergyPanel;
